import { useEffect } from 'react';
import { useParams } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import Navbar from '@/components/feature/Navbar';
import Footer from '@/components/feature/Footer';
import { mainSiteUrl } from '@/mocks/categories';

export default function DisclaimerPage() {
  const { t, i18n } = useTranslation();
  const { lang = 'en' } = useParams<{ lang: string }>();

  useEffect(() => {
    i18n.changeLanguage(lang);
  }, [lang, i18n]);

  return (
    <div className="min-h-screen bg-background-50">
      <Navbar />

      <div className="pt-20 md:pt-24 pb-4 bg-white border-b border-background-200/70">
        <div className="container-main">
          <nav className="flex items-center gap-2 text-sm text-foreground-500">
            <a href={`/${lang}/`} className="hover:text-primary-600 transition-colors">{t('breadcrumb.home')}</a>
            <i className="ri-arrow-right-s-line text-foreground-300"></i>
            <span className="text-foreground-700">Disclaimer</span>
          </nav>
        </div>
      </div>

      <section className="bg-white py-16 md:py-20">
        <div className="container-main text-center">
          <h1 className="font-heading text-3xl md:text-5xl font-bold text-foreground-900 mb-4">
            Disclaimer
          </h1>
          <p className="text-foreground-600 max-w-2xl mx-auto leading-relaxed text-base md:text-lg">
            Important disclaimers regarding the AllChinaBuy Spreadsheet Resource Hub content and its relationship with AllChinaBuy. Last updated: August 25, 2026.
          </p>
        </div>
      </section>

      <section className="bg-background-50 py-12 md:py-16">
        <div className="container-main max-w-4xl">
          <div className="prose-content">
            <h2>Independent Informational Resource</h2>
            <p>
              <strong>AllChinaBuy Spreadsheet Resource Hub (allchinabuyspread.cyou) is an independent informational website and is not affiliated with, endorsed by, or the official website of AllChinaBuy.</strong> We do not represent AllChinaBuy, nor do we have any official partnership, sponsorship, or business relationship with the platform. All opinions, reviews, and information expressed on this Website are our own or based on publicly available information and community feedback.
            </p>

            <h2>No Official Affiliation</h2>
            <p>
              This Website is not the official AllChinaBuy website. The official AllChinaBuy website is accessible through separate channels. All product links on this Website redirect to the main purchasing platform (kaspreadsheet.com) for actual transactions. We do not host, sell, or ship any products directly.
            </p>

            <h2>No Transaction Processing</h2>
            <p>
              We do not process orders, handle payments, manage shipping, or provide customer support for any purchases made through AllChinaBuy. If you have questions about an existing order, payment issue, refund request, or shipping problem, you must contact AllChinaBuy directly through their official support channels. We cannot access your AllChinaBuy account, order history, or transaction details.
            </p>

            <h2>Accuracy and Currency of Information</h2>
            <p>
              While we make reasonable efforts to ensure the accuracy of information on this Website, we cannot guarantee that all content is complete, accurate, reliable, or current at all times. Product prices, availability, descriptions, coupon codes, shipping rates, and other details change frequently and may not reflect the latest information on the AllChinaBuy platform.
            </p>
            <p>
              We strongly encourage all users to verify any information directly with AllChinaBuy or the relevant third-party platform before making purchase decisions. Do not rely solely on information from this Website for financial or purchasing decisions.
            </p>

            <h2>No Professional Advice</h2>
            <p>
              The content on this Website is provided for general informational and educational purposes only. It does not constitute professional, legal, financial, or tax advice. Nothing on this Website should be interpreted as a recommendation to buy, sell, or use any particular product or service. Users should exercise their own judgment and seek professional advice where appropriate.
            </p>

            <h2>User Responsibility</h2>
            <p>
              By using this Website, you acknowledge that you are solely responsible for your purchasing decisions and interactions with AllChinaBuy or any third-party platforms linked from this Website. We are not responsible for any losses, damages, disputes, or negative experiences arising from transactions, product quality, shipping delays, or other issues related to purchases made through AllChinaBuy or any linked platform.
            </p>

            <h2>External Links</h2>
            <p>
              This Website contains links to external websites, including the main AllChinaBuy platform. We are not responsible for the content, accuracy, privacy practices, or terms of service of any third-party websites. We provide these links for convenience and informational purposes only. Visiting external websites is at your own risk, and we encourage you to review their respective terms and policies before engaging with them.
            </p>

            <h2>Reviews and Comparisons</h2>
            <p>
              Reviews, comparisons, and assessments provided on this Website are based on publicly available information, feature comparisons, and general community feedback. They do not represent scientifically rigorous testing or official evaluations. Individual experiences with AllChinaBuy or other platforms may vary significantly. We encourage users to conduct their own research and consider multiple sources before making decisions based on our reviews.
            </p>

            <h2>Intellectual Property</h2>
            <p>
              Product images, brand names, and trademarks displayed on this Website are the property of their respective owners. AllChinaBuy and related branding are trademarks of their respective owners. Our use of these elements is for informational and identification purposes only and does not imply endorsement or affiliation.
            </p>

            <h2>No Liability</h2>
            <p>
              To the fullest extent permitted by applicable law, we disclaim all liability for any direct, indirect, incidental, consequential, or special damages arising from your use of this Website or reliance on any information contained herein. This includes but is not limited to losses related to purchasing decisions, product quality, shipping issues, or financial transactions conducted through third-party platforms.
            </p>

            <h2>Contact for Official Matters</h2>
            <p>
              For official inquiries related to AllChinaBuy products, orders, or services, please contact AllChinaBuy directly through their official website. For questions about this resource hub, content corrections, or feedback, please use our <a href={`/${lang}/contact/`} className="text-primary-600 hover:underline">Contact page</a>.
            </p>
          </div>
        </div>
      </section>

      <section className="bg-white py-12 md:py-16">
        <div className="container-main max-w-4xl">
          <div className="bg-background-50 rounded-2xl p-8 md:p-10 border border-background-200/70 text-center">
            <h3 className="font-heading text-xl md:text-2xl font-bold text-foreground-900 mb-3">
              Ready to Explore AllChinaBuy?
            </h3>
            <p className="text-foreground-600 mb-6 leading-relaxed">
              Browse the spreadsheet, discover products, and make informed purchasing decisions with the resources you have learned here.
            </p>
            <div className="flex flex-col sm:flex-row gap-3 justify-center">
              <a href={`/${lang}/products/`} className="btn-primary inline-flex items-center justify-center">
                View Products <i className="ri-arrow-right-line ml-2"></i>
              </a>
              <a href={mainSiteUrl} target="_blank" rel="noopener nofollow" className="btn-secondary inline-flex items-center justify-center">
                Buy It Now <i className="ri-arrow-right-up-line ml-2"></i>
              </a>
            </div>
          </div>
        </div>
      </section>

      <Footer />
    </div>
  );
}